/**
 * LuviaRx — public "Mark as Shipped" page opened from the admin
 * notification email. Token-gated (no login required). Mobile-first.
 *
 * Flow:
 *   1. GET /api/orders/mark-shipped/{token}       → renders summary
 *   2. Admin picks carrier + pastes tracking #
 *   3. POST /api/orders/mark-shipped/{token}      → flips status to
 *      Shipped/Fulfilled and fires the customer's "Your order shipped"
 *      email with the tracking link.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Package, Truck, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARRIERS = [
  { value: "USPS",  label: "USPS" },
  { value: "UPS",   label: "UPS" },
  { value: "FedEx", label: "FedEx" },
  { value: "DHL",   label: "DHL" },
  { value: "Other", label: "Other" },
];

export default function MarkShipped() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [carrier, setCarrier] = useState("USPS");
  const [tracking, setTracking] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/orders/mark-shipped/${token}`)
      .then(({ data }) => {
        if (cancelled) return;
        setOrder(data);
        if (data.existing_carrier) setCarrier(data.existing_carrier);
        if (data.existing_tracking_number) setTracking(data.existing_tracking_number);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e?.response?.data?.detail || "This link is invalid or expired.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    if (!tracking.trim() || tracking.trim().length < 3) {
      setSubmitError("Please enter a valid tracking number.");
      return;
    }
    setSubmitError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/mark-shipped/${token}`, {
        carrier,
        tracking_number: tracking.trim(),
      });
      setDone(true);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : "Could not mark shipped. Please try again.";
      setSubmitError(msg);
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Shell><div className="text-charcoal-400 text-sm">Loading order…</div></Shell>;
  }
  if (loadError || !order) {
    return (
      <Shell>
        <div data-testid="mark-shipped-error" className="text-center py-6">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" strokeWidth={1.8} />
          <p className="font-display text-2xl text-charcoal-600">Link unavailable</p>
          <p className="text-sm text-charcoal-400 mt-2">{loadError || "Order not found."}</p>
        </div>
      </Shell>
    );
  }
  if (done || order.already_shipped) {
    return (
      <Shell>
        <div data-testid="mark-shipped-success" className="text-center py-6">
          <CheckCircle2 className="w-12 h-12 mx-auto text-sage-600 mb-3" strokeWidth={1.8} />
          <p className="overline mb-1">Shipment confirmed</p>
          <p className="font-display text-3xl text-charcoal-600 mb-2">{order.order_number}</p>
          <p className="text-sm text-charcoal-500 leading-relaxed max-w-xs mx-auto">
            {done
              ? "The customer has been emailed with the tracking number. You're done — you can close this page."
              : "This order was already marked as shipped."}
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <Package className="w-4 h-4 text-[#8B2252]" strokeWidth={2} />
          <p className="overline">Mark as shipped</p>
        </div>
        <p className="font-display text-3xl text-charcoal-600 font-bold">{order.order_number}</p>
        <p className="text-xs text-charcoal-400 mt-1">
          Paid via <span className="font-medium capitalize">{order.payment_method || "zelle"}</span>
          {" · "}
          <span className="font-medium">{order.shipping_method_label || "Standard shipping"}</span>
        </p>
      </header>

      <section className="bg-cream-200/50 rounded-2xl p-4 mb-4 text-sm">
        <p className="text-[10px] uppercase tracking-[0.16em] text-charcoal-300 font-medium mb-1">Ship to</p>
        <p className="font-medium text-charcoal-600">
          {order.customer_name || "—"}
        </p>
        <p className="text-charcoal-400 text-xs">{order.customer_email_masked}</p>
        {order.shipping && (
          <p className="text-charcoal-500 text-xs mt-2 leading-relaxed">
            {order.shipping.address1}
            {order.shipping.address2 ? `, ${order.shipping.address2}` : ""}<br />
            {order.shipping.city}, {order.shipping.state} {order.shipping.zip_code}<br />
            {order.shipping.country || "US"}
          </p>
        )}
      </section>

      <section className="bg-cream-200/50 rounded-2xl p-4 mb-4 text-sm">
        <p className="text-[10px] uppercase tracking-[0.16em] text-charcoal-300 font-medium mb-2">
          Items ({order.items?.length || 0})
        </p>
        <ul className="space-y-1.5">
          {(order.items || []).map((it, idx) => (
            <li key={idx} className="flex justify-between gap-4 text-charcoal-500">
              <span className="truncate">
                <span className="font-medium text-charcoal-600">{it.quantity}×</span>{" "}
                {it.product_name} — {it.variant_label}
              </span>
              <span className="text-charcoal-400 shrink-0">${Number(it.unit_price).toFixed(2)}</span>
            </li>
          ))}
        </ul>
        <p className="text-right mt-3 font-display text-lg text-[#8B2252] font-bold">
          Total ${Number(order.total || 0).toFixed(2)}
        </p>
      </section>

      <div className="mb-4">
        <label
          htmlFor="carrier-select"
          className="block text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-2"
        >
          Carrier
        </label>
        <select
          id="carrier-select"
          data-testid="carrier-select"
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          className="w-full px-4 py-3 rounded-2xl border-2 border-charcoal-100 bg-cream-100 text-charcoal-600 focus:border-[#8B2252] focus:outline-none"
        >
          {CARRIERS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      <div className="mb-5">
        <label
          htmlFor="tracking-input"
          className="block text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-2"
        >
          Tracking number
        </label>
        <input
          id="tracking-input"
          data-testid="tracking-input"
          value={tracking}
          onChange={(e) => setTracking(e.target.value)}
          placeholder="e.g. 9400 1000 0000 0000 0000 00"
          autoComplete="off"
          autoCapitalize="characters"
          className="w-full px-4 py-3 rounded-2xl border-2 border-charcoal-100 bg-cream-100 text-charcoal-600 focus:border-[#8B2252] focus:outline-none tracking-wide"
        />
      </div>

      {submitError && (
        <div
          data-testid="mark-shipped-form-error"
          className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3"
        >
          {submitError}
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={submitting}
        data-testid="mark-shipped-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#2c5f2c] text-white px-6 py-4 rounded-full font-display font-semibold text-base hover:bg-[#234d23] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <Truck className="w-4 h-4" strokeWidth={2} />
        {submitting ? "Marking shipped…" : "Confirm shipment"}
        <ArrowRight className="w-4 h-4" strokeWidth={2} />
      </button>

      <p className="mt-5 text-[11px] text-charcoal-400 text-center leading-relaxed">
        We'll flip this order to <span className="font-medium">Shipped / Fulfilled</span> and email the customer
        with the tracking link.
      </p>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <main data-testid="mark-shipped-page" className="min-h-[calc(100vh-4rem)] bg-cream-200/40 flex items-start justify-center px-4 py-8 md:py-14">
    <div className="w-full max-w-md surface-card p-6 md:p-8">
      {children}
    </div>
  </main>
);
